import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Segmented, Select, Space, Tag } from 'antd';
import toast from 'react-hot-toast';
import { MapPin, Mic, RefreshCw, Type } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { getAdminDesignSystem, ADMIN_ROOT, AdminThemeProvider, getAdminTokens } from './adminDesignSystem';
import AdminSidebar from './AdminSidebar';

const API = '/api/v1/admin-suggestions';

const AdminSuggestions = () => {
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const designSystem = useMemo(() => getAdminDesignSystem(isDark), [isDark]);
  const tk = useMemo(() => getAdminTokens(isDark), [isDark]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, newCount: 0, audioToday: 0 });
  const [status, setStatus] = useState('new');
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [groupKey, setGroupKey] = useState('all');
  const [playing, setPlaying] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API, {
        params: {
          status: status === 'all' ? undefined : status,
          type: type === 'all' ? undefined : type,
          q: q || undefined,
          limit: 100,
        },
      });
      setRows(res.data?.data?.suggestions || []);
      setStats(res.data?.data?.stats || { total: 0, newCount: 0, audioToday: 0 });
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, [api, status, type, q]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.marker ? `${r.pageLabel} · ${r.marker}` : r.pageLabel || r.pagePath;
      if (!map.has(key)) map.set(key, { key, pagePath: r.pagePath, count: 0 });
      map.get(key).count += 1;
    }
    return Array.from(map.values());
  }, [rows]);

  const visible = useMemo(
    () => (groupKey === 'all' ? rows : rows.filter((r) => {
      const key = r.marker ? `${r.pageLabel} · ${r.marker}` : r.pageLabel || r.pagePath;
      return key === groupKey;
    })),
    [rows, groupKey]
  );

  const setRowStatus = async (id, next) => {
    try {
      await api.patch(`${API}/${id}`, { status: next });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: next } : r)));
    } catch (_e) {
      toast.error('Could not update status');
    }
  };

  const playAudio = async (id) => {
    try {
      const res = await api.get(`${API}/${id}/audio`);
      const url = res.data?.data?.url;
      if (!url) throw new Error('no url');
      setPlaying(url);
    } catch (_e) {
      toast.error('Could not play audio');
    }
  };

  const card = {
    background: tk.panel,
    border: `1px solid ${tk.border}`,
    borderRadius: 14,
    padding: 14,
  };

  return (
    <>
      <style>{designSystem}</style>
      <AdminThemeProvider>
        <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden`}>
          <AdminSidebar activeKey="suggestions" />
          <div style={{ flex: 1, overflow: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
              <div>
                <h1 style={{ margin: 0, fontSize: 22, color: tk.text, fontWeight: 650 }}>Suggestions</h1>
                <p style={{ margin: '6px 0 0', color: tk.text2, fontSize: 13 }}>
                  Notes and audio from org admins, grouped by page and marker.
                </p>
              </div>
              <Button icon={<RefreshCw size={14} />} onClick={fetchAll} loading={loading}>Refresh</Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginBottom: 16 }}>
              <div style={card}><div style={{ color: tk.text3, fontSize: 12 }}>New</div><div style={{ fontSize: 22, color: tk.accentTx, fontWeight: 650 }}>{stats.newCount}</div></div>
              <div style={card}><div style={{ color: tk.text3, fontSize: 12 }}>Loaded</div><div style={{ fontSize: 22, color: tk.text, fontWeight: 650 }}>{rows.length}</div></div>
              <div style={card}><div style={{ color: tk.text3, fontSize: 12 }}>Audio today</div><div style={{ fontSize: 22, color: tk.text, fontWeight: 650 }}>{stats.audioToday}</div></div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <Segmented options={[{ label: 'New', value: 'new' }, { label: 'Reviewed', value: 'reviewed' }, { label: 'Done', value: 'done' }, { label: 'All', value: 'all' }]} value={status} onChange={setStatus} />
              <Segmented options={[{ label: 'All types', value: 'all' }, { label: 'Text', value: 'text' }, { label: 'Audio', value: 'audio' }]} value={type} onChange={setType} />
              <Select
                style={{ minWidth: 220 }}
                value={groupKey}
                onChange={setGroupKey}
                options={[{ value: 'all', label: 'All pages / markers' }, ...groups.map((g) => ({ value: g.key, label: `${g.key} (${g.count})` }))]}
              />
              <Input.Search allowClear placeholder="Search org, page, note" style={{ width: 240 }} onSearch={setQ} onChange={(e) => { if (!e.target.value) setQ(''); }} />
            </div>

            {playing && (
              <div style={{ ...card, marginBottom: 12 }}>
                <audio src={playing} controls autoPlay style={{ width: '100%' }} onEnded={() => setPlaying(null)} />
              </div>
            )}

            {loading ? (
              <div style={{ color: tk.text3 }}>Loading…</div>
            ) : visible.length === 0 ? (
              <Empty description="No suggestions yet" />
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {visible.map((r) => (
                  <div key={r.id} style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 650, color: tk.text }}>{r.orgName || 'Organization'}</div>
                        <div style={{ fontSize: 12, color: tk.text2 }}>{r.userName} · {new Date(r.createdAt).toLocaleString()}</div>
                      </div>
                      <Space>
                        <Tag>{r.status}</Tag>
                        {r.type === 'audio' ? <Tag icon={<Mic size={11} />}>Audio</Tag> : <Tag icon={<Type size={11} />}>Text</Tag>}
                      </Space>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: tk.text2, marginBottom: 8 }}>
                      <MapPin size={12} />
                      <span>{r.pageLabel}{r.marker ? ` · ${r.marker}` : ''}</span>
                      <span style={{ color: tk.text3 }}>{r.pagePath}</span>
                    </div>
                    {r.body ? <div style={{ fontSize: 14, color: tk.text, whiteSpace: 'pre-wrap', marginBottom: 10 }}>{r.body}</div> : null}
                    <Space>
                      {r.hasAudio && <Button size="small" onClick={() => playAudio(r.id)}>Play audio</Button>}
                      {r.status !== 'reviewed' && <Button size="small" onClick={() => setRowStatus(r.id, 'reviewed')}>Reviewed</Button>}
                      {r.status !== 'done' && <Button size="small" type="primary" onClick={() => setRowStatus(r.id, 'done')}>Done</Button>}
                      {r.status !== 'new' && <Button size="small" onClick={() => setRowStatus(r.id, 'new')}>Reopen</Button>}
                    </Space>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </AdminThemeProvider>
    </>
  );
};

export default AdminSuggestions;
